import { CONFIG_PARAM } from './UrlConfigUtils';

/** localStorage key holding the most recently seen compressed config blob. */
const STORAGE_KEY = 'faceverify_config';

/**
 * Persists the `?c=<blob>` config param across PWA launches.
 *
 * Runs once on startup, before the existing config pipeline reads the URL:
 * - `?c=<blob>` present (non-empty): save the blob; leave the URL alone.
 * - `?c=` present but empty (RESET signal): clear the saved blob; leave the URL
 *   alone so `readConfigFromUrl()` still sees the empty param and resets.
 * - no `c` param but a blob is saved (bare home-screen launch): re-inject it via
 *   `history.replaceState` so downstream code reads `?c=` normally.
 * - no `c` param and nothing saved: no-op.
 *
 * Any storage/URL failure is logged and swallowed so app boot is never blocked.
 */
export function restoreConfigFromStorage(): void {
    try {
        const params = new URLSearchParams(window.location.search);

        if (params.has(CONFIG_PARAM)) {
            const value = params.get(CONFIG_PARAM) ?? '';
            if (value === '') {
                // Empty ?c= is the RESET signal: clear the saved blob and let the
                // existing pipeline reset to defaults. Leave the URL untouched.
                localStorage.removeItem(STORAGE_KEY);
            } else {
                // Capture the blob so future bare (home-screen) launches can restore it.
                localStorage.setItem(STORAGE_KEY, value);
            }
            return; // URL already carries the param; downstream reads it normally.
        }

        // No config param (e.g. bare PWA launch) — restore from storage if present.
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            const url = new URL(window.location.href);
            url.searchParams.set(CONFIG_PARAM, saved);
            window.history.replaceState(null, '', url); // downstream sees ?c= normally
        }
    } catch (error) {
        console.error('ConfigPersistence: failed to restore config from storage', error);
    }
}
